<template>
  <q-page padding class="text-center">
    <h2>Backups</h2>

    <q-btn flat icon="add" class="full-width q-mb-md" @click="makeNewBackup">Create New Backup</q-btn>

    <q-table
      :data="backups"
      :columns="tableColumns"
      dense
      :loading="$apollo.loading"
      :pagination.sync="tablePagination"
      row-key="name" >
      <template v-slot:body-cell-actions="props">
        <q-td :props="props">
          <q-btn flat dense icon="restore" @click="restoreBackup(props.row)"/>
        </q-td>
      </template>

    </q-table>

  </q-page>
</template>

<script>
import gql from 'graphql-tag'
import saveAs from 'file-saver'
import { formatDateTime } from '../../util/string'

export default {
  name: 'PageAdminBackups',
  apollo: {
    backups () {
      return {
        query: gql`
          query {
            listBackups {
              name
              sizeInBytes
              date
            }
          }`,
        fetchPolicy: 'network-only',
        update (result) {
          return result.listBackups
        }
      }
    }
  },
  computed: {
    hasBackupAccess () {
      return this.currentAccount && this.currentAccount.roles.includes('backupAccess')
    },
    currentAccount () {
      return this.$store.state.auth.currentAccount
    }
  },
  methods: {
    async disconnect () {
      await this.$apollo.mutate({
        mutation: gql`mutation disconnectXero {
          disconnectXero
        }`
      })
      this.xeroConnected = false
    },
    async connect () {
      const link = await this.$apollo.query({
        query: gql`query getXeroConnectionLink {
          getXeroConnectionLink
        }`,
        fetchPolicy: 'network-only'
      })
      const connectWindow = window.open(link.data.getXeroConnectionLink, 'Connect ICP to Xero', 'width=500,height=600')
      this.$apollo.queries.xeroConnected.startPolling(1000)
      while (true) {
        if (this.xeroConnected) break
        if (connectWindow.closed) break
        await new Promise(resolve => setTimeout(resolve, 10))
      }
      connectWindow.close()
      this.$apollo.queries.xeroConnected.stopPolling()
    },
    async downloadProductsCSV () {
      const result = await this.$apollo.query({
        query: gql`query productsCsv {
          productsCsv
        }`,
        fetchPolicy: 'network-only'
      })
      saveAs(new Blob([result.data.productsCsv], { type: 'application/octet-stream' }), `ICP_Products.csv`)
    },
    async restoreBackup (backup) {
      this.$q.dialog({
        title: 'Restore Backup',
        message: `Are you sure you want to restore the backup ${backup.name} from ${formatDateTime(backup.date)}?`,
        cancel: true
      }).onOk(() => {
        this.$q.dialog({
          title: 'Are You Sure?',
          message: `This will completely replace data in the site with that from the backup (though a new backup will be made prior to restoring).
Are you sure you want to continue?`,
          cancel: true
        }).onOk(() => {
          this.restoreBackupWithoutPrompt(backup.name)
        })
      })
    },
    async makeNewBackup () {
      await this.$apollo.mutate({
        mutation: gql`mutation createBackup {
          createBackup
        }`
      })
      this.$apollo.queries.backups.refetch()
      this.$q.dialog({
        title: 'Backup Created!',
        message: `Successfully created a new backup.`
      })
    },
    async restoreBackupWithoutPrompt (backupName) {
      await this.$apollo.mutate({
        mutation: gql`mutation restoreBackup ( $backupName: String ) {
          restoreBackup ( backupName: $backupName )
        }`,
        variables: {
          backupName
        }
      })
      this.$apollo.queries.backups.refetch()
      this.$q.dialog({
        title: 'DB Restored!',
        message: `The backup ${backupName} has been restored!`
      })
    }
  },
  data () {
    return {
      tablePagination: { rowsPerPage: 20, sortBy: 'date', descending: true },
      tableColumns: [
        { name: 'date', label: 'Date', align: 'left', field: 'date', sortable: true, format: formatDateTime },
        { name: 'size', label: 'Size', align: 'left', field: 'sizeInBytes', sortable: true, format: v => `${(v / 1024).toFixed(0)} KB` },
        { name: 'name', label: 'Name', align: 'left', field: 'name', sortable: true }
        // Hidden backups due to problems with mongoDB versions and backup restoration.  Just do it 'manually' with the scripts in icpportal.
        // { name: 'actions', label: 'Actions', align: 'left', sortable: false }
      ]
    }
  }
}
</script>
